import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { onboardingRoute } from '@app/core/session/onboarding-route';
import { TokenStatus } from '@app/core/session/onboarding-session.model';
import { OnboardingSessionStore } from '@app/core/session/onboarding-session.store';
import { TokenStatusApi } from './data/token-status.api';

export interface AccessGuardOptions {
  /** La ruta exige que el cliente ya haya validado su RFC (ej. `documents`). */
  requiresRfc?: boolean;
}

/**
 * Guard de entrada al flujo (plan 6.3). Solo ordena la navegación: la protección real
 * de los datos la tiene que hacer el backend.
 *
 * Lee `:token` de `/onboarding/:token/...` y redirige siempre dentro del mismo token:
 * 1. Sin token → `/not-found`.
 * 2. Mismo token ya validado como `in-progress` → no repite la request (y conserva los intentos de RFC gastados).
 * 3. Si no, consulta el estado: `completed` → `completed`, `expired` o cualquier error → `expired`.
 * 4. Sin intentos de RFC → `locked`: el backend lo rechazaría aunque sea correcto.
 * 5. Con `requiresRfc` y RFC sin validar → `rfc`.
 */
export function onboardingAccessGuard({ requiresRfc = false }: AccessGuardOptions = {}): CanActivateFn {
  return (route) => {
    const router = inject(Router);
    const store = inject(OnboardingSessionStore);
    const api = inject(TokenStatusApi);

    const token = route.paramMap.get('token');
    if (!token) {
      return router.parseUrl('/not-found');
    }

    return resolveStatus(token, store, api).pipe(
      map((status) => {
        if (status === 'completed') return router.createUrlTree(onboardingRoute(token, 'completed'));
        if (status === 'expired') return router.createUrlTree(onboardingRoute(token, 'expired'));
        if (store.rfcLocked()) return router.createUrlTree(onboardingRoute(token, 'locked'));
        if (requiresRfc && !store.rfcVerified()) {
          return router.createUrlTree(onboardingRoute(token, 'rfc'));
        }
        return true;
      }),
      // Decisión de negocio: cualquier error (4xx, 5xx, red, respuesta rara) se muestra como vencido.
      catchError(() => of(router.createUrlTree(onboardingRoute(token, 'expired')))),
    );
  };
}

function resolveStatus(token: string, store: OnboardingSessionStore, api: TokenStatusApi): Observable<TokenStatus> {
  if (store.token() === token && store.status() === 'in-progress') {
    return of('in-progress');
  }
  // start() antes de la request: resetea la sesión si el token cambió, así el `setStatus` de la respuesta cae en la sesión nueva;
  // además el mock local lee el token del store.
  store.start(token);
  return api.getStatus(token).pipe(
    tap(({ status, rfcChallenge }) => store.setStatus(status, rfcChallenge)),
    map(({ status }) => status),
  );
}
